import axios, { AxiosError, AxiosResponse, CancelToken } from 'axios';
import blobErrorToObject from 'helpers/blobErrorToObject';
import { ISignUpData } from 'interfaces';

type SignResponseType = {
  user: ISignUpData;
  accessToken: string;
};

type LoginResponseType = {
  access_token: string;
  refresh_token: string;
  message?: string;
};

export type InviteSignupResponse = {
  email?: string;
  room_id: null | string;
  invite_id: null | string;
  group_id: null | string;
  type: 'common';
  is_registered?: boolean;
  is_parent_invite?: boolean;
};

type AcceptResponse = {
  access_token: string;
  refresh_token: string
}

type ResetResponse = {
  success: boolean,
  message: string,
}

const instance = axios.create({
  baseURL: window.REACT_APP_SERVER_API !== 'REPLACE_REACT_APP_SERVER_API' ? window.REACT_APP_SERVER_API : process.env.REACT_APP_SERVER_API || 'http://localhost:8000/api',
  headers: { 'Access-Control-Allow-Origin': '*' },
  withCredentials: true,
});

instance.interceptors.response.use(( response: AxiosResponse ) => {
  return response
}, async (error: AxiosError<any>) => {
  if (error?.response?.data.constructor.name === 'Blob'){
    error.response.data = await blobErrorToObject(error?.response?.data)
  };
  return Promise.reject(error)
});

const login = (payload: FormData) => instance.post<LoginResponseType>('/users/login/?action=create', payload);
const signup = (payload: ISignUpData, browser_data: string) => instance.post<SignResponseType>('/users/register/?action=create', payload, { params: {browser_data} });
const inviteSignup = (payload: string) => instance.get<InviteSignupResponse>(`/invites/invite/data/?jwt_token=${payload}&action=view`);

const acceptRegistration = (
  browser_data: string, user_id: string, link_id: string, room_id?: string, signal?: AbortSignal
) => instance.post<AcceptResponse>(`/users/register/accept/`,
  { browser_data },  { signal, params: {user_id, browser_data, link_id, room_id} }
);

const sendEmailToRestore = (payload: { email: string }) => instance.post<ResetResponse>('/users/send-restore/', payload); 
const restorePassword = (payload: { user_id: string, password: string, link_id: string }) => instance.post<ResetResponse>('/users/restore/', payload);
const refreshTokens = (payload: { refresh_token: string }) => instance.post<LoginResponseType>('/users/refresh-token/', payload);
const decodeDataFromLink = (jwt_token: string) => instance.get<any>(`/organizations/form/data/?jwt_token=${jwt_token}`);
const sendMessageToSupport = (payload: FormData) => instance.post<any>(`/support/`, payload);
const verifyAuth = (
  payload: { email: string; auth_password: string; browser_data: string },
  formData: FormData,
  token?: CancelToken
) =>
  instance.post<LoginResponseType>(
    `/users/login/verify/?email=${payload.email}&auth_password=${payload.auth_password}`,
    formData,
    { cancelToken: token }
  );
const validateLink = (link: string) =>
    instance.post<{ is_valid: boolean; used_at: string }>(`/users/link/validate/?link_id=${link}`);
const resendConfirmationLink = (payload: { email: string }) => instance.post<any>('/users/resend-confirmation/', payload);
const checkUserByEmail = (payload: { email: string }) => instance.get(`/users/email/check/?email=${payload.email}`);
const externalAuthLogin = (payload: any) => instance.post(`/external-auth/login/`, payload);
const externalAuthRegister = (payload: any) => instance.post(`/external-auth/register/`, payload);
const checkPassword = (password: string, token: string) => instance.get<ResetResponse>(`/users/password/check/`, {
  params: { password },
  headers: { Authorization: token }
}); 

const auth = {
  login,
  signup,
  inviteSignup,
  acceptRegistration,
  sendEmailToRestore,
  restorePassword,
  refreshTokens,
  decodeDataFromLink,
  sendMessageToSupport,
  verifyAuth,
  validateLink,
  resendConfirmationLink,
  checkUserByEmail,
  externalAuthLogin,
  externalAuthRegister,
  checkPassword,
};

export default auth;
